import { useMemo, useState } from "react";
import { useAppSelector } from "../../../state/store";
import { Check } from "../../../components/Check/Check";
import { Row } from "../../../components/Row";
import { DataTable } from "../../../components/DataTable/DataTable";
import { DropdownMenu } from "../../../components/Dropdown/DropdownMenu";
import * as skTypes from "../../../types/sk";
import * as utils from "../../../util/skUtils";
import "./PlayByPlay.scss";

interface PBPProps {
  moments: skTypes.Moment[];
  box: skTypes.BoxScore;
  teams: skTypes.Team[];
  onClose: () => void;
  onChangeMoments: (newMoments: skTypes.Moment[]) => void;
  onEdit: (momentIndex: number) => void;
  editable: boolean;
  isPractice?: boolean;
}

/**
 * Shows a list of all moments in a quiz, with options to edit them
 */
export function PlayByPlay(props: PBPProps) {
  interface MomentWithQuestion {m: skTypes.Moment, qNum: number}
  const momentsWithQuestionIndex = useMemo(() => {
    const result: MomentWithQuestion[] = []
    let qNum = 1;
    props.moments.forEach((m) => {
      result.push({
        m,qNum
      })
      if (utils.isQuestion(m)) qNum += 1;
    })
    return result
  },[ props.moments])
  const { material } = useAppSelector((state) => state.material);
  const { skAdvanced } = useAppSelector((state) => state.settings);

  const [showAll, setShowAll] = useState<boolean>(true);
  type ColorTypeOptions = "RESULT" | "TEAM" | "JUMP";
  const [colorType, setColorType] = useState<ColorTypeOptions | undefined>(
    "RESULT"
  );
  const scoresByMoment: skTypes.RunningScoreItem[] = utils.getRunningScore(
    props.teams,
    props.moments,
    true
  );

  const [momentSelected, setMomentSelected] = useState<Set<number>>(new Set());
  const [switchEnabled, setSwitchEnabled] = useState<boolean>(false);

  // Used to map for options to change what happened
  interface MomentTypeChangeOption {
    type: skTypes.QuestionType | skTypes.KErrorType;
    isErrorType: boolean;
    isKErrorType: boolean;
  }
  const momentTypeChangeOptions: Readonly<MomentTypeChangeOption[]> = [
    { type: skTypes.tp, isErrorType: false, isKErrorType: false },
    ...(!skAdvanced
      ? [
          {
            type: skTypes.error,
            isErrorType: false,
            isKErrorType: false,
          },
        ]
      : [
          {
            type: skTypes.light,
            isErrorType: true,
            isKErrorType: false,
          },
          {
            type: skTypes.split,
            isErrorType: true,
            isKErrorType: false,
          },
          { type: skTypes.ref, isErrorType: true, isKErrorType: false },
          {
            type: skTypes.fifty,
            isErrorType: true,
            isKErrorType: false,
          },
          {
            type: skTypes.wrongVerse,
            isErrorType: false,
            isKErrorType: true,
          },
          {
            type: skTypes.wrongWord,
            isErrorType: false,
            isKErrorType: true,
          },
          {
            type: skTypes.invalidQuestion,
            isErrorType: false,
            isKErrorType: true,
          },
          {
            type: skTypes.outOfTime,
            isErrorType: false,
            isKErrorType: true,
          },
          {
            type: skTypes.badCall,
            isErrorType: false,
            isKErrorType: true,
          },
        ]),
    { type: skTypes.foul, isErrorType: false, isKErrorType: false },
  ];

  let questionNum = 0;

  const executeMove = (dir: "UP" | "DOWN") => {
    const newMoments: skTypes.Moment[] = [...props.moments];
    let selectedArray = Array.from(momentSelected).sort();
    if (dir === "DOWN") selectedArray = selectedArray.reverse();
    const indexDiff = dir === "UP" ? -1 : 1;
    selectedArray.forEach((momentIndex) => {
      if (
        (momentIndex === 0 && dir === "UP") ||
        (momentIndex === newMoments.length - 1 && dir === "DOWN")
      )
        return;
      const temp = newMoments[momentIndex];
      newMoments[momentIndex] = newMoments[momentIndex + indexDiff];
      newMoments[momentIndex + indexDiff] = temp;
    });

    props.onChangeMoments(newMoments);
    for (let i = 0; i < selectedArray.length; i++) {
      if (
        (selectedArray[i] === 0 && dir === "UP") ||
        (selectedArray[i] === newMoments.length - 1 && dir === "DOWN")
      )
        continue;
      selectedArray[i] += indexDiff;
    }
    setMomentSelected(new Set(selectedArray));
  };

  return (
    <div className="page" style={{ minWidth: 700 }}>
      <Row>
        <button className="secondary-btn" onClick={props.onClose}>
          Go Back
        </button>
        <Check
          checked={showAll}
          onClick={setShowAll}
          style={{ marginTop: 0, marginLeft: 10, marginRight: 10, width: 110 }}
        >
          Show All
        </Check>
        {momentSelected.size > 0 ? (
          <>
            <button
              className="link"
              style={{ marginRight: 5 }}
              onClick={() => {
                const newMoments: skTypes.Moment[] = [];
                props.moments.forEach((moment, index) => {
                  if (!momentSelected.has(index)) newMoments.push(moment);
                });
                props.onChangeMoments(newMoments);
                setMomentSelected(new Set());
              }}
            >
              Delete Selected
            </button>
            {momentSelected.size === 1 && (
              <button
                className="link"
                style={{ marginRight: 5 }}
                onClick={() => props.onEdit(Array.from(momentSelected)[0])}
                data-testid="pbp-edit"
              >
                Edit
              </button>
            )}
            {!switchEnabled ? (
              <>
                <button
                  className="link"
                  style={{ marginRight: 5 }}
                  onClick={() => executeMove("UP")}
                >
                  Move Up
                </button>
                <button
                  className="link"
                  style={{ marginRight: 5 }}
                  onClick={() => executeMove("DOWN")}
                >
                  Move Down
                </button>
                {momentSelected.size === 1 && (
                  <button
                    className="link"
                    style={{ marginRight: 5 }}
                    onClick={() => setSwitchEnabled(true)}
                    key="start-switch"
                    data-testid="pbp-switch"
                  >
                    Switch with another item
                  </button>
                )}
              </>
            ) : (
              <>
                <span style={{ paddingTop: 10, marginRight: 5 }}>
                  Select another item to switch with.
                </span>
                <button
                  className="link"
                  onClick={() => setSwitchEnabled(false)}
                  key="cancel-switch"
                >
                  Cancel Switching
                </button>
              </>
            )}
          </>
        ) : (
          <>
            {skAdvanced && (
              <>
                <div style={{ marginTop: 10, marginLeft: 10, marginRight: 5 }}>
                  Color Code:
                </div>
                <Check
                  checked={colorType === "RESULT"}
                  onClick={(e) => setColorType(e ? "RESULT" : undefined)}
                  style={{ width: 90, marginTop: 0, marginRight: 10 }}
                >
                  Results
                </Check>
                <Check
                  checked={colorType === "JUMP"}
                  onClick={(e) => setColorType(e ? "JUMP" : undefined)}
                  style={{ width: 90, marginTop: 0, marginRight: 10 }}
                >
                  Jump
                </Check>
                {!props.isPractice && (
                  <Check
                    checked={colorType === "TEAM"}
                    onClick={(e) => setColorType(e ? "TEAM" : undefined)}
                    style={{ width: 90, marginTop: 0, marginRight: 10 }}
                  >
                    Team
                  </Check>
                )}
              </>
            )}
          </>
        )}
      </Row>
      <hr className="separator-line" />
      <DataTable<MomentWithQuestion>
        data={momentsWithQuestionIndex}
        columns={[
          {
            title: "#",
            property: "questionNum",
            render: (obj) => {
              return obj.qNum
            },
          },
          {
            title: "Team",
            property: "teamName",
            visible: !props.isPractice,
            render: ({m: obj}, index) => {
              const teamName =
                "team" in obj && obj.team !== undefined
                  ? props.teams[obj.team].teamName
                  : "";
              return "team" in obj && obj.team !== undefined ? (
                props.editable ? (
                  <DropdownMenu
                    id={`pbp-teamname-${index}`}
                    items={props.teams.map((team, teamIndex, arr) => ({
                      title: team.teamName,
                      onClick: () => {
                        const moments: skTypes.Moment[] = [...props.moments];
                        const newMoment = {
                          ...obj,
                          team: teamIndex,
                        };

                        if ("quizzerId" in newMoment)
                          newMoment.quizzerId =
                            props.teams[teamIndex].quizzers[0].quizzerId;

                        moments[index] = newMoment;
                        props.onChangeMoments(moments);
                      },
                    }))}
                  >
                    {teamName}
                  </DropdownMenu>
                ) : (
                  teamName
                )
              ) : (
                ""
              );
            },
          },
          {
            title: "Quizzer",
            property: "quizzerName",
            render: ({m: obj}, index) => {
              if (!("quizzerId" in obj)) return null;
              const quizzerId = obj.quizzerId;
              const quizzerName =
                obj.team !== undefined && quizzerId !== undefined
                  ? props.box.teams[obj.team].quizzers[quizzerId]?.quizzerName
                  : "";
              return obj.team !== undefined && quizzerId ? (
                props.editable ? (
                  <DropdownMenu
                    items={props.teams[obj.team].quizzers.map((quizzer) => ({
                      title: quizzer.quizzerName,
                      onClick: () => {
                        const moments: skTypes.Moment[] = [...props.moments];
                        moments[index] = {
                          ...obj,
                          quizzerId: quizzer.quizzerId,
                        };
                        props.onChangeMoments(moments);
                      },
                    }))}
                    id={`pbp-quizzer-${index}`}
                  >
                    {quizzerName}
                  </DropdownMenu>
                ) : (
                  quizzerName
                )
              ) : (
                ""
              );
            },
          },
          {
            title: "Happened",
            property: "happened",
            render: ({m: obj}, index) => {
              // Get what happened
              let happened: string =
                "errorType" in obj && skAdvanced
                  ? skTypes.errorDescription[obj.errorType!]
                  : skTypes.momentTypeDescriptions[obj.type] || obj.type;
              if ("kErrorType" in obj && skAdvanced)
                happened = `${happened} (${
                  skTypes.kErrorDescription[obj.kErrorType!]
                })`;
              if (!props.isPractice) {
                // Quiz Out or Error Out
                if (scoresByMoment[index].quizErrorOut)
                  happened = happened.concat(
                    ` (${obj.type === "TWENTY_POINTS" ? "Q" : "E"}O)`
                  );
                // Bonus
                if (
                  obj.type === "TWENTY_POINTS" &&
                  obj.team !== undefined &&
                  questionNum > 1 &&
                  scoresByMoment[index].scores[obj.team] ===
                    scoresByMoment[index - 1].scores[obj.team]! + 40
                )
                  happened = happened.concat(" (B)");
              }

              return "quizzerId" in obj && props.editable ? (
                <DropdownMenu
                  items={momentTypeChangeOptions.map((option) => {
                    const momentType: skTypes.MomentType =
                      option.isErrorType || option.isKErrorType
                        ? "ERROR"
                        : (option.type as skTypes.MomentType);
                    const errorType: skTypes.ErrorType | undefined =
                      option.isErrorType
                        ? (option.type as skTypes.ErrorType)
                        : option.isKErrorType
                          ? "K"
                          : undefined;
                    const kErrorType: skTypes.KErrorType | undefined =
                      option.isKErrorType
                        ? (option.type as skTypes.KErrorType)
                        : undefined;
                    return {
                      title: `${
                        kErrorType
                          ? `K-Error${
                              kErrorType
                                ? ` (${skTypes.kErrorDescription[kErrorType]})`
                                : ""
                            }`
                          : ""
                      }
                      ${
                        errorType && !kErrorType
                          ? skTypes.errorDescription[errorType]
                          : ""
                      }
                      ${
                        !errorType
                          ? skTypes.momentTypeDescriptions[momentType]
                          : ""
                      }`,
                      onClick: () => {
                        const moments: skTypes.Moment[] = [...props.moments];
                        moments[index].type = momentType;
                        const thisMoment = moments[index];
                        if (thisMoment.type === "ERROR") {
                          thisMoment.errorType = errorType;
                          if (thisMoment.errorType === "K") {
                            thisMoment.kErrorType = kErrorType;
                          } else if ("kErrorType" in thisMoment) {
                            delete thisMoment.kErrorType;
                          }
                        }

                        props.onChangeMoments(moments);
                      },
                    };
                  })}
                  id={`pbp-happened-${index}`}
                >
                  {happened}
                </DropdownMenu>
              ) : (
                happened
              );
            },
          },
          ...props.teams.map((team, teamIndex) => ({
            title: team.teamName,
            property: `team-${team.teamId}`,
            visible: !props.isPractice,
            render: (_: MomentWithQuestion, index: number) =>
              scoresByMoment[index].scores[teamIndex],
          })),
          {
            title: "Book",
            property: "book",
            render: ({m: obj}) =>
              "book" in obj && obj.book !== undefined
                ? material[obj.book]?.bookName || ""
                : "",
          },
          {
            title: "Ch.",
            property: "chapter",
            render: ({m: obj}) =>
              "chapter" in obj && obj.chapter !== undefined && obj.chapter >= 0
                ? obj.chapter + 1
                : "",
          },
          {
            title: "V.",
            property: "verse",

            render: ({m: obj}) =>
              "verse" in obj && obj.verse !== undefined && obj.verse >= 0
                ? obj.verse + 1
                : "",
          },
          {
            title: "Question",
            property: "question",
            visible: skAdvanced,
            render: ({m: obj}) => {
              if (!("question" in obj)) return "";
              return (
                <>
                  {obj.receivedCharacters !== undefined &&
                    obj.receivedCharacters < 0 && <span>[S]&nbsp;</span>}
                  {obj.question && (
                    <>
                      <span className="received-characters">
                        {obj.question.substring(0, obj.receivedCharacters)}
                      </span>
                      {obj.receivedCharacters !== undefined && (
                        <span>
                          {obj.question.substring(obj.receivedCharacters || 0)}
                        </span>
                      )}
                      {obj.read && <span>&nbsp;[R]</span>}
                    </>
                  )}
                </>
              );
            },
          },
        ]}
        rowDisplay={({m: obj}, index) => {
          const getRowClassName = (): string | undefined => {
            if (momentSelected.size > 0) return undefined;
            switch (colorType) {
              case "JUMP":
                if (
                  !(obj.type === "TWENTY_POINTS" || obj.type === "ERROR") ||
                  (obj.verse === undefined && obj.receivedCharacters !== -1)
                )
                  return undefined;
                const { words, read } = utils.getWordsReceived(obj);
                return words > 0 || read ? "pbp-green" : "pbp-red";
              case "RESULT":
                return obj.type === "TWENTY_POINTS"
                  ? "pbp-green"
                  : obj.type === "ERROR"
                    ? "pbp-red"
                    : undefined;
              case "TEAM":
                if (
                  !(obj.type === "TWENTY_POINTS" || obj.type === "ERROR") ||
                  obj.team === undefined
                )
                  return undefined;
                return `pbp-${utils.teamSideColors[obj.team]}`;
            }
          };
          const isShown: boolean =
            showAll ||
            utils.isQuestion(obj) ||
            [skTypes.startOvertime, skTypes.halftime].includes(obj.type);
          return {
            className: getRowClassName(),
            selected: momentSelected.has(index),
            style: {
              display: !isShown ? "none" : undefined,
            },
          };
        }}
        onClickCell={(_, index, column) => {
          if (column === "questionNum" && props.editable) {
            const selectedMoment = Array.from(momentSelected)[0];
            if (momentSelected.size > 0 && switchEnabled && props.editable) {
              const newMoments: skTypes.Moment[] = [...props.moments];
              const temp = newMoments[index];
              newMoments[index] = newMoments[selectedMoment];
              newMoments[selectedMoment] = temp;
              props.onChangeMoments(newMoments);
              setMomentSelected(new Set());
              setSwitchEnabled(false);
            } else {
              const newSelected = new Set(momentSelected);
              if (newSelected.has(index)) {
                newSelected.delete(index);
              } else {
                newSelected.add(index);
              }
              setMomentSelected(newSelected);
            }
          }
        }}
        style={{
          minHeight: "calc(100vh - 120px)",
          maxHeight: "calc(100vh - 120px)",
          marginBottom: 30,
        }}
      />
    </div>
  );
}
